/**
 * cpn-search.js
 * ─────────────
 * Runs on Shopify search results pages. Calls the Cloudflare Worker to check
 * whether the search query matches an alternate customer part number, then
 * appends matching products to the native results — without replacing them.
 */

(function () {
  'use strict';

  // ─── Configuration ──────────────────────────────────────────────────────────
  const CONFIG = {
    // Your deployed Cloudflare Worker URL
    workerUrl: 'https://cpn-search.solymardigital.workers.dev/',

    // Storefront API public access token
    storefrontToken: '038a04aceedacb5a2cb500b5471c9005',

    // Your store's myshopify domain
    storeDomain: 'shopvelvac.myshopify.com',

    // CSS selector for the native search results wrapper
    resultsSelector: '.product-grid',

    // CSS selector for the "no results" message — update if needed
    noResultsSelector: 'p[role="status"]',
 
    // CSS selector for native product cards (for deduplication)
    nativeCardSelector: '[data-product-handle]',
  };

  // ─── Only run on the /search path ───────────────────────────────────────────
  if (!window.location.pathname.startsWith('/search')) return;
 
  const params = new URLSearchParams(window.location.search);
  const rawQuery = (params.get('q') || '').trim();
  if (!rawQuery) return;
 
  // ─── Storefront API query ────────────────────────────────────────────────────
  const PRODUCT_QUERY = `
    query GetProduct($handle: String!) {
      product(handle: $handle) {
        id
        title
        handle
        description(truncateAt: 150)
        availableForSale
        featuredImage {
          url(transform: { maxWidth: 1024 })
          altText
        }
        variants(first: 1) {
          nodes { id availableForSale sku price { amount currencyCode }}
        }
        uom: metafield(namespace: "custom", key: "uom") {
            value
            type
        }
      }
    }
  `;
 
  // ─── Fetch product by handle from Storefront API ─────────────────────────────
  async function fetchProduct(handle) {
    const res = await fetch(
      `https://${CONFIG.storeDomain}/api/2026-01/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken,
        },
        body: JSON.stringify({
          query: PRODUCT_QUERY,
          variables: { handle },
        }),
      }
    );
 
    if (!res.ok) throw new Error(`Storefront API error: ${res.status}`);
    const { data } = await res.json();
    return data?.product || null;
  }
 
  // ─── Get handles already shown by native search ───────────────────────────────
  function getNativeHandles() {
    const handles = new Set();
    document.querySelectorAll(CONFIG.nativeCardSelector).forEach(el => {
      if (el.dataset?.productHandle) {
        handles.add(el.dataset.productHandle);
      }
    });
    // Fallback: parse product URLs
    document.querySelectorAll('a[href*="/products/"]').forEach(el => {
      const match = (el.href || '').match(/\/products\/([^?#/]+)/);
      if (match) handles.add(match[1]);
    });
    return handles;
  }
 
  // ─── Build product card matching Dawn theme markup ────────────────────────────
  function buildCard(product) {
    const image = product.featuredImage;
    const available = product.availableForSale;
 
    // Build srcset matching Dawn's responsive image pattern
    const widths = [165, 360, 533, 720, 940, 1024];
    const srcset = image
      ? widths.map(w => `${image.url}&width=${w} ${w}w`).join(',')
      : '';
 
    const imgHtml = image
      ? `<img
           srcset="${escapeHtml(srcset)}"
           src="${escapeHtml(image.url)}&width=533"
           sizes="(min-width: 1200px) 267px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
           alt="${escapeHtml(image.altText || product.title)}"
           class="motion-reduce"
           width="1024"
           height="1024"
           loading="lazy"
         >`
      : '';
 
    const badgeHtml = !available
      ? `<div class="card__badge bottom left">
           <span class="badge badge--bottom-left color-scheme-2">Sold out</span>
         </div>`
      : `<div class="card__badge bottom left"></div>`;
 
    return `
      <li class="grid__item scroll-trigger animate--slide-in cpn-result-item">
        <div class="card-wrapper product-card-wrapper underline-links-hover"
             data-product-handle="${escapeHtml(product.handle)}">
          <div class="card card--card card--media color-scheme-2 gradient"
               style="--ratio-percent: 100.0%;">
            <div class="card__inner ratio" style="--ratio-percent: 100.0%;">
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  ${imgHtml}
                </div>
              </div>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="/products/${escapeHtml(product.handle)}"
                       class="full-unstyled-link">
                      ${escapeHtml(product.title)}
                    </a>
                  </h3>
                  <p class="product__sku">SKU: ${escapeHtml(product.variants.nodes[0].sku)}</p>
                </div>
                ${badgeHtml}
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="/products/${escapeHtml(product.handle)}"
                     class="full-unstyled-link">
                    ${escapeHtml(product.title)}
                  </a>
                </h3>
                <p class="product__sku">SKU: ${escapeHtml(product.variants.nodes[0].sku)}</p>
                <div class="card-information">
                    <div class="price ">
                        <div class="price__container">
                            <div class="price__regular">
                                <span class="visually-hidden visually-hidden--inline">Regular price</span>
                                <span class="price-item price-item--regular">$${escapeHtml(product.variants.nodes[0].price.amount)}</span><span class="product-uom" style="text-transform:lowercase;">/ ${escapeHtml(product.uom.value) }</span>
                            </div>
                        </div>
                    </div>
                </div>
              </div>
              ${badgeHtml}
            </div>
          </div>
        </div>
      </li>
    `;
  }
 
  // ─── Hide the native "no results" message ────────────────────────────────────
  function hideNoResults() {
    document.querySelectorAll(CONFIG.noResultsSelector).forEach(el => {
      el.style.display = 'none';
    });
  }
 
  // ─── Main flow ────────────────────────────────────────────────────────────────
  async function run() {
    // 1. Ask the Worker if this query is a known alternate part number
    let workerData;
    try {
      const workerRes = await fetch(
        `${CONFIG.workerUrl}?q=${encodeURIComponent(rawQuery)}`,
        { signal: AbortSignal.timeout(3000) }
      );
 
      if (!workerRes.ok) return;
      workerData = await workerRes.json();
    } catch {
      return;
    }
 
    const { handle } = workerData;
    if (!handle) return;
 
    // 2. Don't show a product the native search already found
    const nativeHandles = getNativeHandles();
    if (nativeHandles.has(handle)) return;
 
    // 3. Fetch full product details from Storefront API
    let product;
    try {
      product = await fetchProduct(handle);
    } catch {
      return;
    }
 
    if (!product) return;
 
    // 4. Find the product grid
    const grid = document.querySelector(CONFIG.resultsSelector);
    if (!grid) return;
 
    // 5. Inject the card as the first item in the grid
    grid.insertAdjacentHTML('afterbegin', buildCard(product));
 
    // 6. Hide the "no results" message if present
    hideNoResults();
  }
 
  // ─── Bootstrap ───────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
 
  // ─── Utility ─────────────────────────────────────────────────────────────────
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
})();